import {useCallback, useEffect, useReducer, useRef, useState} from 'react';

import {Point} from 'util/geometry/GeometryConstants';
import {assertNever, ElementMouseEvent, ElementWheelEvent} from 'util/Types';

import {DWD_MAX_COLLAPSED_TABLE_HEIGHT, DWD_TABLE_WIDTH} from './diagram-layout/diagramLayoutConstants';

export enum PanningActionType {
  MOVE = 'MOVE',
  SET = 'SET',
}

type PanningAction =
  | {type: PanningActionType.MOVE; mouseDelta: Point}
  | {type: PanningActionType.SET; newTranslation: Point};

export function panningReducer(translation: Point, action: PanningAction): Point {
  switch (action.type) {
    case PanningActionType.MOVE: {
      return {x: translation.x + action.mouseDelta.x, y: translation.y + action.mouseDelta.y};
    }
    case PanningActionType.SET: {
      return action.newTranslation;
    }
    default: {
      throw new Error(`Invalid action type ${assertNever(action)}`);
    }
  }
}

/**
 * @param translation: The current translation of the diagram.
 * @param isPanning: Whether or not the user is panning around the diagram. Set to true when a mousedown
 *     event starts within the diagram, and ends once the mouse is released anywhere on the screen.
 * @param centerLayout: Sets translation so that the diagram's focus table is at the visual center
 *     of the diagram's div.
 * @param startDiagramPan: Sets isPanning to true and adds event listeners to the window to listen to
 *     mousemove and mouseup events. mousemove events trigger updates to translation, and a mouseup event
 *     triggers isPanning to return to false and the event listeners to be removed.
 */
interface DiagramPanningStateOutput {
  translation: Point;
  isPanning: boolean;
  centerLayout: (wrapperDiv: React.RefObject<HTMLDivElement>) => void;
  startDiagramPan: (event: Pick<ElementMouseEvent, 'clientX' | 'clientY'>) => void;
  shiftDiagramByWheelEvent: (event: Pick<ElementWheelEvent, 'deltaX' | 'deltaY'>) => void;
}

export default function useDiagramPanningState(): DiagramPanningStateOutput {
  // useReducer and useState are both async, which means that when we "update" the state with them
  // it doesn't happen instantly. This makes useState problematic because we are continuously
  // updating the translation state while panning, and when we update we send the new
  // translation based on the current translation, which means multiple updates in a row are
  // made from the same translation before the first update comes in. This causes the panning
  // diagram to not move. useReducer in this case acts more like a "queue", we send it a list
  // of delta vectors to apply to the state, and it updates the state with those vectors
  // based on the value at the time of update.
  const [translation, dispatchPanningAction] = useReducer(panningReducer, {x: 0, y: 0});
  const [isPanning, setIsPanning] = useState(false);

  const previousMousePosition = useRef<Point>({x: 0, y: 0});

  // TODO(BNCH-15222) - Refactor to share functionality with useDnDMouseMoveHandler.
  const diagramPan = useCallback((event: MouseEvent) => {
    const mouseDelta = {
      x: event.clientX - previousMousePosition.current.x,
      y: event.clientY - previousMousePosition.current.y,
    };

    dispatchPanningAction({
      type: PanningActionType.MOVE,
      mouseDelta,
    });

    previousMousePosition.current = {
      x: event.clientX,
      y: event.clientY,
    };

    event.preventDefault();
  }, []);

  const endDiagramPan = useCallback(() => {
    setIsPanning(false);

    window.removeEventListener('mousemove', diagramPan);
    window.removeEventListener('mouseup', endDiagramPan);
  }, [diagramPan]);

  const startDiagramPan = useCallback(
    (event: Pick<ElementMouseEvent, 'clientX' | 'clientY'>) => {
      setIsPanning(true);

      previousMousePosition.current = {
        x: event.clientX,
        y: event.clientY,
      };
      window.addEventListener('mousemove', diagramPan);
      window.addEventListener('mouseup', endDiagramPan);
    },
    [diagramPan, endDiagramPan]
  );

  // Remove event listeners if component unmounts without calling `endDiagramPan`.
  useEffect(
    () => () => {
      window.removeEventListener('mousemove', diagramPan);
      window.removeEventListener('mouseup', endDiagramPan);
    },
    [diagramPan, endDiagramPan]
  );

  /**
   * Centers the diagram so that the focus table is at the visual center of the diagram's div.
   *
   * x translation computed as half the diagram width minus half a table's width.
   * y translation computed as half the diagram height minus half a table's collapsed height.
   */
  const centerLayout = useCallback((diagramRef: React.RefObject<HTMLDivElement>) => {
    if (diagramRef.current) {
      dispatchPanningAction({
        type: PanningActionType.SET,
        newTranslation: {
          x: Math.floor(diagramRef.current.offsetWidth / 2 - DWD_TABLE_WIDTH / 2),
          y: Math.floor(diagramRef.current.offsetHeight / 2 - DWD_MAX_COLLAPSED_TABLE_HEIGHT / 2),
        },
      });
    }
  }, []);

  const shiftDiagramByWheelEvent = useCallback((event: Pick<ElementWheelEvent, 'deltaX' | 'deltaY'>) => {
    dispatchPanningAction({
      type: PanningActionType.MOVE,
      mouseDelta: {
        x: -event.deltaX,
        y: -event.deltaY,
      },
    });
  }, []);

  return {
    translation,
    isPanning,
    centerLayout,
    startDiagramPan,
    shiftDiagramByWheelEvent,
  };
}
